import React, { useCallback, useEffect, useState } from "react";
import {
  FileText,
  Loader2,
  Trash2,
  Upload,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import {
  deleteEmployeeHrDocument,
  getEmployeeHrDocuments,
  uploadEmployeeHrDocument,
} from "../../../api/hrm/employees/employeeInfo";
import { showConfirmDialog, showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { hasEmployeeSensitivePermission } from "../../../utils/permissionUtils";

export const HR_DOC_TYPE_OPTIONS = [
  { value: "labor_contract", label: "Hợp đồng lao động" },
  { value: "job_application", label: "Hồ sơ xin việc" },
  { value: "interview_result", label: "Kết quả phỏng vấn" },
  { value: "id_card", label: "CCCD / CMND" },
  { value: "diploma", label: "Bằng cấp / chứng chỉ" },
  { value: "other", label: "Khác" },
];

const labelOf = (type) =>
  HR_DOC_TYPE_OPTIONS.find((o) => o.value === type)?.label || type || "Khác";

const formatSize = (n) => {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("vi-VN");
  } catch {
    return iso;
  }
};

/**
 * Upload / danh sách tài liệu hồ sơ nhân sự (tab Hợp đồng).
 */
const EmployeeHrDocumentsPanel = ({ employeeId, canEdit, showPayrollHint = true }) => {
  const canView =
    canEdit ||
    hasEmployeeSensitivePermission("R") ||
    hasEmployeeSensitivePermission("U");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("labor_contract");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId || !canView) return;
    setLoading(true);
    try {
      const list = await getEmployeeHrDocuments(employeeId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (e) {
      setDocs([]);
      showErrorToast(
        e?.response?.data?.message || e?.message || "Không tải được danh sách tài liệu."
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId, canView]);

  useEffect(() => {
    load();
  }, [load]);

  if (!employeeId || !canView) return null;

  const handleUpload = async () => {
    if (!file) {
      showErrorToast("Vui lòng chọn file");
      return;
    }
    setUploading(true);
    try {
      await uploadEmployeeHrDocument({
        employee_id: employeeId,
        file,
        doc_type: docType,
        title,
        note,
      });
      setFile(null);
      setTitle("");
      setNote("");
      showSuccessToast("Đã tải lên tài liệu");
      await load();
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message || e?.message || "Upload thất bại"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    const result = await showConfirmDialog({
      title: "Xóa tài liệu?",
      text: `Xóa “${doc.title || doc.file_name}”? Thao tác không hoàn tác.`,
      confirmText: "Xóa",
      cancelText: "Huỷ",
    });
    if (!result?.isConfirmed) return;
    try {
      await deleteEmployeeHrDocument(doc.id);
      showSuccessToast("Đã xóa tài liệu");
      await load();
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message || e?.message || "Không xóa được tài liệu"
      );
    }
  };

  return (
    <div className="space-y-4">
      {showPayrollHint ? (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-3.5 py-2.5 text-xs leading-relaxed text-blue-900">
          Kho lưu trữ hồ sơ nhân sự (có thể nhiều file cùng loại). Upload file{" "}
          <b>không</b> tự cập nhật mức lương — vui lòng nhập riêng ở mục{" "}
          <b>Thông tin HĐ lao động</b> bên dưới (khi chỉnh sửa nhân viên).
        </p>
      ) : null}
      {canEdit ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
            <Upload className="h-4 w-4 text-red-600" />
            Tải lên tài liệu nhân sự
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-[10px] font-bold uppercase text-gray-400 space-y-1 block">
              Loại tài liệu
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
              >
                {HR_DOC_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px] font-bold uppercase text-gray-400 space-y-1 block">
              Tiêu đề (tuỳ chọn)
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="VD: HĐLĐ 2026"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
              />
            </label>
          </div>
          <label className="text-[10px] font-bold uppercase text-gray-400 space-y-1 block">
            Ghi chú
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú ngắn…"
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
            />
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="flex-1 cursor-pointer rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 hover:border-red-300 transition-colors">
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <span className="font-semibold text-gray-800">{file.name}</span>
              ) : (
                <span>Chọn file (PDF, Word, ảnh… — tối đa 20MB)</span>
              )}
            </label>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !file}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-xs font-bold uppercase tracking-wide text-white hover:bg-red-700 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Tải lên
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
          Danh sách đã lưu ({docs.length})
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Làm mới
        </button>
      </div>

      {loading && docs.length === 0 ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
          Chưa có tài liệu. Tải lên HĐLĐ, hồ sơ xin việc, kết quả phỏng vấn…
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li
              key={doc.id}
              className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
            >
              <div className="mt-0.5 rounded-xl bg-red-50 p-2 text-red-600">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600">
                    {labelOf(doc.doc_type)}
                  </span>
                  <span className="truncate text-sm font-bold text-gray-900">
                    {doc.title || doc.file_name}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-xs text-gray-400">
                  {doc.file_name} · {formatSize(doc.file_size)} · {formatDate(doc.created_at)}
                  {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ""}
                </p>
                {doc.note ? (
                  <p className="mt-1 text-xs text-gray-500">{doc.note}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {doc.file_url ? (
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                    title="Mở file"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => handleDelete(doc)}
                    className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Xóa"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmployeeHrDocumentsPanel;
